import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ApiService } from '../../services/api.service';
import { AgentTask, AgentTasksResponse } from '../../models/agent-task.model';
import { WorkstationRoleType } from '../../models/client-management.model';

@Component({
  selector: 'app-agent-tasks',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './agent-tasks.component.html',
  styleUrls: ['./agent-tasks.component.scss']
})
export class AgentTasksComponent implements OnInit {
  tasks: AgentTask[] = [];
  loading = false;
  error: string | null = null;

  // Paging
  currentPage = 1;
  pageSize = 20;
  totalCount = 0;
  totalPages = 0;

  // Filters
  databases: string[] = [];
  roleTypes: WorkstationRoleType[] = [];
  selectedDatabase = '';
  selectedRoleId: number | null = null;
  selectedStatus: number | null = null;

  statusOptions: { value: number | null; label: string }[] = [
    { value: null,  label: 'All Statuses' },
    { value: 0,     label: 'Pending' },
    { value: 10,    label: 'Running' },
    { value: 100,   label: 'Success' },
    { value: 99,    label: 'Error' },
  ];

  // Log modal
  logModalVisible = false;
  logLoading = false;
  logError: string | null = null;
  logHtml: SafeHtml = '';
  selectedTaskId: number | null = null;

  constructor(private apiService: ApiService, private sanitizer: DomSanitizer) {}

  ngOnInit(): void {
    this.loadFilters();
    this.loadTasks();
  }

  loadFilters(): void {
    this.apiService.getTrackedDatabases().subscribe({
      next: (dbs) => this.databases = dbs,
      error: (err) => console.error('Error loading databases:', err)
    });
    this.apiService.getRoleTypes().subscribe({
      next: (roles) => this.roleTypes = roles,
      error: (err) => console.error('Error loading role types:', err)
    });
  }

  loadTasks(): void {
    this.loading = true;
    this.error = null;
    this.apiService.getAgentTasks(
      this.selectedDatabase || undefined,
      this.selectedRoleId ?? undefined,
      this.selectedStatus ?? undefined,
      this.currentPage,
      this.pageSize
    ).subscribe({
      next: (response: AgentTasksResponse) => {
        this.tasks = response.data;
        this.totalCount = response.totalCount;
        this.totalPages = Math.ceil(response.totalCount / this.pageSize);
        this.loading = false;
      },
      error: () => {
        this.error = 'Failed to load agent tasks';
        this.loading = false;
      }
    });
  }

  onFilterChange(): void {
    this.currentPage = 1;
    this.loadTasks();
  }

  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.loadTasks();
    }
  }

  getPageNumbers(): number[] {
    const pages: number[] = [];
    const start = Math.max(1, this.currentPage - 2);
    const end = Math.min(this.totalPages, this.currentPage + 2);
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  }

  getStatusLabel(status: number): string {
    switch (status) {
      case 0:   return 'Pending';
      case 10:  return 'Running';
      case 100: return 'Success';
      case 99:  return 'Error';
      default:  return String(status);
    }
  }

  getStatusClass(status: number): string {
    switch (status) {
      case 0:   return 'status-pending';
      case 10:  return 'status-running';
      case 100: return 'status-success';
      case 99:  return 'status-error';
      default:  return '';
    }
  }

  getPublishLabel(status: number): string {
    switch (status) {
      case 0:   return '—';
      case 100: return 'Published';
      case 99:  return 'Error';
      default:  return String(status);
    }
  }

  viewLog(task: AgentTask): void {
    this.selectedTaskId = task.taskID;
    this.logModalVisible = true;
    this.logLoading = true;
    this.logError = null;
    this.logHtml = '';

    this.apiService.getAgentTaskLog(task.taskID).subscribe({
      next: (text: string) => {
        this.logHtml = this.sanitizer.bypassSecurityTrustHtml(this.ansiToHtml(text));
        this.logLoading = false;
      },
      error: () => {
        this.logError = 'Failed to load log file';
        this.logLoading = false;
      }
    });
  }

  closeLog(): void {
    this.logModalVisible = false;
    this.logHtml = '';
    this.selectedTaskId = null;
  }

  private ansiToHtml(text: string): string {
    const colorMap: Record<number, string> = {
      30: '#000', 31: '#cc3333', 32: '#33cc33', 33: '#cccc33',
      34: '#3333cc', 35: '#cc33cc', 36: '#33cccc', 37: '#cccccc',
      90: '#888', 91: '#ff5555', 92: '#55ff55', 93: '#ffff55',
      94: '#5555ff', 95: '#ff55ff', 96: '#55ffff', 97: '#fff',
    };

    let html = text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');

    let open = false;
    html = html.replace(/\x1b\[([0-9;]*)m/g, (_: string, codes: string) => {
      const nums = codes === '' ? [0] : codes.split(';').map(Number);
      if (nums.includes(0)) {
        if (open) { open = false; return '</span>'; }
        return '';
      }
      const fg = nums.find((n: number) => (n >= 30 && n <= 37) || (n >= 90 && n <= 97));
      if (fg !== undefined) {
        const color = colorMap[fg] ?? '#ccc';
        const prefix = open ? '</span>' : '';
        open = true;
        return `${prefix}<span style="color:${color}">`;
      }
      return '';
    });

    if (open) html += '</span>';
    // Remove any remaining unrecognized escape sequences
    html = html.replace(/\x1b\[[0-9;]*[A-Za-z]/g, '');
    return html;
  }
}
